/**
 * Base API service for making HTTP requests to the backend
 */
import { getToken } from '../utils/auth';

// Set the API URL explicitly to ensure consistent behavior
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:3000/api');

/**
 * Handles API responses and errors
 * @param {Response} response - Fetch response object
 * @returns {Promise<any>} - Parsed response data
 */
const handleResponse = async (response) => {
  const text = await response.text();
  
  // If the response is empty, return an empty object
  if (!text) {
    return {};
  }
  
  // Try to parse as JSON, but handle non-JSON responses
  let data;
  try {
    data = JSON.parse(text);
  } catch (error) {
    // If the response is not valid JSON, throw an error with the text
    if (!response.ok) {
      throw new Error(`Server error: ${text.substring(0, 100)}`);
    }
    // If the response is OK but not JSON, return the text
    return { message: text };
  }
  
  if (!response.ok) {
    const error = data.message || data.error || response.statusText;
    throw new Error(error);
  }
  
  return data;
};

/**
 * Creates request options for fetch API
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} body - Request body (for POST/PUT requests)
 * @param {Object} customHeaders - Custom headers to override defaults
 * @returns {Object} - Request options
 */
const createRequestOptions = (method, body, customHeaders = {}) => {
  const token = getToken();
  const headers = {};
  
  // Don't set Content-Type for FormData - let browser set it with boundary
  if (!(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Add authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Apply custom headers (but don't override FormData Content-Type)
  Object.keys(customHeaders).forEach(key => {
    if (key !== 'Content-Type' || !(body instanceof FormData)) {
      headers[key] = customHeaders[key];
    }
  });
  
  const options = {
    method,
    headers,
    credentials: 'include' // Include cookies in requests
  };
  
  // Add body for POST/PUT requests
  if (body && (method === 'POST' || method === 'PUT')) {
    if (body instanceof FormData) {
      options.body = body; // Don't stringify FormData
    } else {
      options.body = JSON.stringify(body);
    }
  }
  
  return options;
};

/**
 * Base API service with methods for common HTTP operations
 */
const apiService = {
  /**
   * Performs a GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Optional configuration with params, headers, etc.
   * @returns {Promise<any>} - Response data
   */
  async get(endpoint, config = {}) {
    const options = createRequestOptions('GET');
    
    // Handle query parameters if provided
    let url = `${API_URL}${endpoint}`;
    if (config.params) {
      const queryString = new URLSearchParams(config.params).toString();
      url += (endpoint.includes('?') ? '&' : '?') + queryString;
    }
    
    const response = await fetch(url, options);
    return handleResponse(response);
  },
  
  /**
   * Performs a POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} customHeaders - Custom headers to override defaults
   * @returns {Promise<any>} - Response data
   */
  async post(endpoint, body, customHeaders = {}) {
    const options = createRequestOptions('POST', body, customHeaders);
    const response = await fetch(`${API_URL}${endpoint}`, options);
    return handleResponse(response);
  },
  
  /**
   * Performs a PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @returns {Promise<any>} - Response data
   */
  async put(endpoint, body) {
    const options = createRequestOptions('PUT', body);
    const response = await fetch(`${API_URL}${endpoint}`, options);
    return handleResponse(response);
  },
  
  /**
   * Performs a DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<any>} - Response data
   */
  async delete(endpoint) {
    const options = createRequestOptions('DELETE');
    const response = await fetch(`${API_URL}${endpoint}`, options);
    return handleResponse(response);
  }
};

export default apiService;
